<template>
  <div class="running-invoices">
    <div class="component-header">
      {{ $t('running_invoices') }}
    </div>

    <v-card :loading="isLoading" class="elevation-0">
      <div
        v-if="isLoading || billingAccounts.length > 1"
        class="running-invoices__header"
      >
        {{ $t('choose_billing_account') }}:

        <v-select
          v-model="country"
          color="primary"
          class="running-invoices__select"
          :items="billingAccounts"
          item-title="name"
          return-object
          :max-width="166"
          hide-details
        />
      </div>

      <v-divider
        v-if="isLoading || billingAccounts.length > 1"
        class="ml-4 mr-4"
      />

      <!-- not empty -->
      <div v-if="activeBillingAccount.length">
        <div
          v-for="(account, i) in activeBillingAccount"
          :key="i"
          class="running-invoices__account"
        >
          <div class="running-invoices__total">
            <div>
              <div class="running-invoices__total__title">
                <div>
                  {{ $t('total_balance') }}
                </div>

                <span>
                  {{ $t('min_invoice_limit') }} {{ account.invoice_limit }}{{ account.currency.symbol }}

                  <Tooltip
                    :text="$t('running_invoice_minimum_limit')"
                    :link="learnMoreLink"
                    position="top"
                    :delay="1000"
                  />
                </span>
              </div>

              <div class="running-invoices__total__price">
                {{ account.subtotal_amount }}{{ account.currency.symbol }}
              </div>
            </div>

            <span
              text
              class="running-invoices__total__show-detail"
              @click="toggleShowMore(i)"
            >
              <template v-if="!showMore[i]">
                {{ $t('view_details') }}
                <v-icon>
                  add
                </v-icon>
              </template>
              <template v-else>
                {{ $t('hide_details') }}
                <v-icon>
                  remove
                </v-icon>
              </template>
            </span>
          </div>

          <div class="running-invoices__info">
            <div
              v-for="(item, j) in account.countries"
              :key="j"
              class="running-invoices__countries"
            >
              <div class="running-invoices__countries__label">
                <span>
                  {{ item.country.label }}
                </span>

                <span>
                  {{ item.amount }}{{ item.country.currency.symbol }}
                </span>
              </div>

              <transition-group name="slide-down">
                <span
                  v-for="(period, k) in showMore[i] ? item.running_invoices : []"
                  :key="k"
                  class="running-invoices__details"
                >
                  {{ period.period }}

                  <span
                    v-for="(invoice, h) in period.invoices"
                    :key="h"
                    class="running-invoices__details__item"
                  >
                    <span class="running-invoices__details__type">
                      <strong>
                        {{ invoice.type }}
                      </strong>
                      ({{ invoice.units }} {{ impressionsOrClickouts(invoice.type) }})
                    </span>

                    <span class="running-invoices__details__price">
                      {{ invoice.amount }}{{ item.country.currency.symbol }}
                    </span>
                  </span>
                </span>
              </transition-group>
            </div>
          </div>
        </div>
      </div>

      <!-- empty -->
      <div v-if="!isLoading && billingAccounts.length === 0">
        <div class="running-invoices__account">
          <div class="running-invoices__total">
            <div>
              <div class="running-invoices__total__title">
                <div>{{ $t('total_balance') }}</div>

                <span>
                  {{ $t('min_invoice_limit') }} 100

                  <Tooltip
                    :text="$t('running_invoice_minimum_limit')"
                    :link="learnMoreLink"
                    position="top"
                    :delay="1000"
                  />
                </span>
              </div>

              <div class="running-invoices__total__price">
                0
              </div>
            </div>
          </div>
        </div>
      </div>
    </v-card>
  </div>
</template>

<script>
  import Tooltip from '../tooltips/Tooltip';
  import { isNonEmptyArr } from '../../utils';

  export default {
    components: {
      Tooltip
    },
    emits: ['onChangeLoadingStatus'],
    data() {
      return {
        country: '',
        learnMoreLink: import.meta.env.VUE_APP_HELP_BILLING_URL,
        accounts: [],
        showMore: [],
        isLoading: true,
      };
    },
    computed: {
      billingAccounts() {
        return this.$store.getters['invoices/accounts'];
      },
      activeBillingAccount() {
        const arr = this.$store.getters['invoices/activeAccount'];

        arr.forEach((_, index) => {
          this.showMore[index] = false;
        });

        return arr;
      }
    },
    watch: {
      country(val) {
        this.isLoading = true;
        const query = {
          id: val.id
        };

        this.$store.dispatch('invoices/getAccount', query)
          .then(() => { this.isLoading = false; return false; })
          .catch(() => { this.isLoading = false; return false; });
      },
      billingAccounts(arr) {
        if (isNonEmptyArr(arr)) {
          [this.country] = arr; // after we get accounts, set the first one as a default value
        }
      },
      isLoading(val) {
        this.$emit('onChangeLoadingStatus', val);
      }
    },
    created() {
      return this.$store.dispatch('invoices/getAccounts')
        .then(() => {
          if (this.billingAccounts.length === 0) {
            this.isLoading = false;
            return false;
          }
          return false;
        })
        .catch(() => { this.isLoading = false; return false; });
    },
    methods: {
      toggleShowMore(index) {
        this.showMore[index] = !this.showMore[index];
        this.$forceUpdate();
      },
      impressionsOrClickouts(type) {
        if (type === 'CPC') return 'Clickouts';
        return 'Impressions';
      }
    }
  };
</script>

<style lang="scss">
@use 'sass:map';
@use '../../assets/styles/theme' as *;

.running-invoices {
  width: 100%;

  .component-header {
    font-size: 18px;
    font-weight: 700;
    padding: 0 0 16px;
  }

  &__header {
    align-items: center;
    color: map.get($colors, grey, darken-1);;
    display: flex;
    font-size: 14px;
    font-weight: 400;
    justify-content: space-between;
    padding: 16px 14px;
  }

  &__account {
    display: flex;
    flex-direction: column;
    padding: 14px 16px;
  }

  &__details {
    display: flex;
    flex-direction: column;
    font-weight: 500;
    font-size: 14px;
    margin: 10px 0 0;

    &__item {
      color: #757575;
      display: flex;
      justify-content: space-between;
    }

    &__type {
      font-weight: 400;

      strong {
        font-weight: 600;
      }
    }

    &__price {
      font-weight: 500;
    }
  }

  &__select {
    max-width: calc(100% - 161px);
  }

  &__total {
    display: flex;
    justify-content: space-between;
    background-color: #F5F5F5;
    border-radius: $border-radius;
    flex-direction: column;
    margin: 0 0 8px;
    padding: 28px 16px 13px;

    > div {
      display: flex;
      justify-content: space-between;
    }

    &__title {
      color: #424242;
      display: flex;
      flex-direction: column;
      font-size: 16px;
      letter-spacing: -0.1px;

      div {
        line-height: 1.1;
      }

      span {
        color: #626262;
        font-size: 12px;
        line-height: 1.6;
        letter-spacing: -0.1px;
        font-weight: 400;

        i {
          color: currentColor !important;
        }
      }
    }

    &__price {
      font-size: 30px;
      color: #424242;
      line-height: 1.2;
    }

    &__show-detail {
      cursor: pointer;
      font-size: 14px;
      font-weight: 700;
      line-height: 1.8;
      margin: 6px 0 0 auto;

      .v-icon {
        color: map.get($colors, text);
      }
    }
  }

  &__countries {
    border-top: 1px solid #E6E6E6;
    display: flex;
    flex-direction: column;
    font-weight: 700;
    justify-content: space-between;
    padding: 16px 0 16px;

    &__label {
      display: flex;
      justify-content: space-between;
    }

    &:first-child {
      border-top: none;
      padding: 0 0 16px;
    }
  }

  &__info {
    border: 1px solid #E0E0E0;
    border-radius: $border-radius;
    padding: 14px 14px 0;
  }

  @media (max-width: map.get($breakpoints, md)) {
    width: 100%;
  }
}
</style>
